import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnalysisEntry, DocumentVersion } from '@/types/documents';
import { API_V1 } from '@/utils/apiBase';

interface VersionHistoryState {
  versions: DocumentVersion[];
  loading: boolean;
  error: string | null;
}

/**
 * State holds locale keys and raw API fields; every translated string is built
 * at render, so a language change re-translates without refetching.
 */
export function useVersionHistory(
  documentId: string | undefined,
  analysisHistory: AnalysisEntry[],
): VersionHistoryState {
  const { t } = useTranslation();
  const [rows, setRows] = useState<DocumentVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // A caller that omits `analysisHistory` gets VersionHistoryView's `= []`
  // default, a fresh array every render: depending on that identity made each
  // fetch's own setState re-fire the effect (261 requests in 400ms, unbounded).
  // Key on the fields rendered downstream, so this cannot mask a real change.
  const analysisKey = analysisHistory
    .map((e) => `${e.id}:${e.type}:${e.version}:${e.created_at}:${e.created_by}`)
    .join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const analysis = useMemo(() => analysisHistory, [analysisKey]);

  useEffect(() => {
    const fetchVersionHistory = async () => {
      if (!documentId) return;

      try {
        setLoading(true);
        setErrorKey(null);

        const token = sessionStorage.getItem('access_token');

        const response = await fetch(`${API_V1}/documents/${documentId}/versions`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          throw Object.assign(new Error(`version history request failed (${response.status})`), {
            status: response.status,
          });
        }

        const data = await response.json();
        const apiVersions = data.versions || [];
        const currentVersionNumber = data.currentVersion || 1;

        const transformedVersions: DocumentVersion[] = apiVersions.map((v: any) => {
          const versionUploadTime = new Date(v.uploadedAt);
          const relatedAnalysis = analysis.filter((entry) => {
            const analysisTime = new Date(entry.created_at);
            return analysisTime >= versionUploadTime;
          });

          return {
            id: `v${v.versionNumber}`,
            versionNumber: v.versionNumber,
            fileName: v.fileName,
            fileSize: v.fileSize,
            uploadedBy: v.uploadedBy || '',
            uploadedAt: v.uploadedAt,
            changesSummary: v.changeLog || '',
            isCurrentVersion: v.versionNumber === currentVersionNumber,
            downloadUrl: v.path,
            relatedAnalysis,
          };
        });

        transformedVersions.sort((a, b) => b.versionNumber - a.versionNumber);

        setRows(transformedVersions);
        setLoading(false);
      } catch (err) {
        // No failure degrades into sample data. The fabricated rows below used
        // to render for anything that was not a 401 whenever the document had
        // analysis history — i.e. exactly where a user is most likely to trust
        // them — with only a console.warn. Branch on status, never on the
        // message: the message is translated copy.
        const status = (err as { status?: number }).status;
        console.error('Failed to load version history:', err);
        setErrorKey(
          status === 401 ? 'errors.authenticationRequired' : 'documents.versionHistoryLoadFailed',
        );
        setLoading(false);
      }
    };

    if (documentId) {
      fetchVersionHistory();
    }
  }, [documentId, analysis]);

  const versions = useMemo(
    () =>
      rows.map((version) => ({
        ...version,
        uploadedBy: version.uploadedBy || t('common.unknown'),
        changesSummary: version.changesSummary || t('documents.noDescription'),
      })),
    [rows, t],
  );

  return { versions, loading, error: errorKey ? t(errorKey) : null };
}
